/**
 * App Shell Component
 * 
 * Main application wrapper with sidebar navigation and header
 * Handles responsive layout and mobile drawer
 */

import { useState } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import ClassicSidebar from './ClassicSidebar';
import Header from './Header';
import { useTheme } from '@/contexts/ThemeContext';

export default function AppShell() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { isClassic } = useTheme();
  const location = useLocation(); // ✅ Move to top level - must be called unconditionally

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const toggleCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  if (isClassic) {
    // Classic MARS2 Layout
    // location already declared above at top level
    
    // Main navigation tabs matching actual routes
    const mainTabs = [
      { path: '/', label: 'Dashboard', section: '/' },
      { path: '/certificates', label: 'Certificates', section: '/certificates' },
      { path: '/maintenance/equipment', label: 'Maintenance', section: '/maintenance' },
      { path: '/crew/personnel', label: 'Crew', section: '/crew' },
      { path: '/safety/inspections', label: 'Safety', section: '/safety' },
      { path: '/inventory', label: 'Inventory', section: '/inventory' },
      { path: '/documents', label: 'Documents', section: '/documents' },
      { path: '/reports', label: 'Reports', section: '/reports' },
      { path: '/settings', label: 'Configuration', section: '/settings' },
    ];

    const isTabActive = (section: string) => {
      if (section === '/') {
        return location.pathname === '/';
      }
      return location.pathname.startsWith(section);
    };

    return (
      <div className="theme-classic flex flex-col h-screen overflow-hidden bg-white">
        <Header
          onMenuClick={toggleSidebar}
          sidebarCollapsed={sidebarCollapsed}
        />
        
        {/* Classic Top Menu Tabs - Functional Navigation */}
        <div className="classic-top-menu">
          {mainTabs.map((tab) => (
            <NavLink
              key={tab.section}
              to={tab.path}
              className={`classic-tab ${isTabActive(tab.section) ? 'active' : ''}`}
            >
              {tab.label}
            </NavLink>
          ))}
        </div>

        {/* Classic Split View: Tree + Content */}
        <div className="classic-split-view flex-1 overflow-hidden">
          {/* Classic Tree Sidebar - Contextual Document Browser */}
          <div className="classic-split-left">
            <ClassicSidebar />
          </div>

          {/* Classic Content Area */}
          <div className="classic-split-right">
            <div className="classic-content-area h-full">
              <Outlet />
            </div>
          </div>
        </div>

        {/* Classic Status Bar */}
        <div className="classic-status-bar">
          <div className="classic-status-item">
            <span>Ready</span>
          </div>
          <div className="classic-status-separator" />
          <div className="classic-status-item">
            <span>Database: Connected</span>
          </div>
          <div className="classic-status-separator" />
          <div className="classic-status-item">
            <span>User: {sidebarOpen ? 'Active' : 'Idle'}</span>
          </div>
        </div>
      </div>
    );
  }

  // Modern Layout
  return (
    <div className={`flex h-screen overflow-hidden bg-gray-50`}>
      {/* Sidebar for desktop */}
      <div
        className={`
          hidden md:flex md:flex-shrink-0
          transition-all duration-300 ease-in-out
          ${sidebarCollapsed ? 'md:w-20' : 'md:w-64'}
        `}
      >
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggleCollapse={toggleCollapse}
          onClose={closeSidebar}
        />
      </div>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 md:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={`
          fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out md:hidden
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <Sidebar
          collapsed={false}
          onToggleCollapse={toggleCollapse}
          onClose={closeSidebar}
          isMobile
        />
      </div>

      {/* Main content area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header
          onMenuClick={toggleSidebar}
          sidebarCollapsed={sidebarCollapsed}
        />

        {/* Page content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden bg-gray-50">
          <div className="container mx-auto px-4 py-6 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

